import { create } from '@bufbuild/protobuf'
import {
  DefaultWithdrawOffset,
  type DefaultWithdrawOffsetSetting,
} from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { UpdateDefaultWithdrawOffsetRequestSchema } from '@ebalance/proto/bffuser/v1/bid_limit_service_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  NativeSelectField,
  NativeSelectIndicator,
  NativeSelectRoot,
  Text,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactAlert } from '~/components/common/CompactAlert'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { appToaster } from '~/libs/toaster'
import { useUpdateDefaultWithdrawOffset } from '~/pages/AdminBidLimits/hooks/useBidLimitSettings'
import { FIRST_SAVE_EXPECTED_UPDATED_AT } from '~/pages/AdminBidLimits/models/constants'

// 5 択の順序は spec (bidding/bid-limits.md §4 AC-BDL-501) の表示順と一致させる。
// 確認テキスト (AC-BDL-502) も選択肢ごとの i18n key で持つ。
const OFFSET_OPTIONS: ReadonlyArray<{
  value: DefaultWithdrawOffset
  labelKey: string
  confirmKey: string
}> = [
  {
    value: DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_IMMEDIATE,
    labelKey: 'page:adminBidLimits.defaultWithdrawOffset.optionImmediate',
    confirmKey: 'page:adminBidLimits.defaultWithdrawOffset.confirmImmediate',
  },
  {
    value: DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_30_MIN,
    labelKey: 'page:adminBidLimits.defaultWithdrawOffset.option30Min',
    confirmKey: 'page:adminBidLimits.defaultWithdrawOffset.confirm30Min',
  },
  {
    value: DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_1_HOUR,
    labelKey: 'page:adminBidLimits.defaultWithdrawOffset.option1Hour',
    confirmKey: 'page:adminBidLimits.defaultWithdrawOffset.confirm1Hour',
  },
  {
    value: DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_1_HOUR_30_MIN,
    labelKey: 'page:adminBidLimits.defaultWithdrawOffset.option1Hour30Min',
    confirmKey: 'page:adminBidLimits.defaultWithdrawOffset.confirm1Hour30Min',
  },
  {
    value: DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_2_HOUR,
    labelKey: 'page:adminBidLimits.defaultWithdrawOffset.option2Hour',
    confirmKey: 'page:adminBidLimits.defaultWithdrawOffset.confirm2Hour',
  },
]

// ensure_exists default (row 不在) は backend / BFF どちらも IMMEDIATE を
// 返すが、GetBidLimitMatrixScreen が INTRADAY タブ自体を読み込めていない
// 場合 (setting が undefined) の表示防御として frontend 側にも既定値を持つ。
const DEFAULT_VALUE = DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_IMMEDIATE

type Props = {
  setting: DefaultWithdrawOffsetSetting | undefined
  /**
   * workflow-back-bar-readonly AC-17: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき選択操作を disabled にする。
   */
  isReadonly?: boolean
}

/**
 * デフォルト取り下げ時刻カード (bidding/bid-limits.md §4)。
 *
 * AC-BDL-501: 5 段階の選択肢を表示する。
 * AC-BDL-502: 選択時に即保存し、確認テキストを画面下部に表示する。
 * AC-BDL-503: 「直前」選択中は注意警告を表示する。
 */
export const DefaultWithdrawOffsetCard = ({
  setting,
  isReadonly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { readonlyReason } = useWorkflowLockReasons()
  const { mutateAsync, isPending } = useUpdateDefaultWithdrawOffset()

  const currentValue = setting?.value ?? DEFAULT_VALUE
  const currentOption = OFFSET_OPTIONS.find((opt) => opt.value === currentValue)
  const isImmediate =
    currentValue === DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_IMMEDIATE

  const handleChange = async (next: DefaultWithdrawOffset): Promise<void> => {
    try {
      await mutateAsync(
        create(UpdateDefaultWithdrawOffsetRequestSchema, {
          value: next,
          // 初回保存 (setting 未取得) は epoch 番兵を送る (= backend が None 扱い)。
          expectedUpdatedAt:
            setting?.updatedAt ?? FIRST_SAVE_EXPECTED_UPDATED_AT,
        }),
      )
      // 即時保存にも成功 toast を出す (MarketOrderPolicyCard と同じ理由:
      // 単発トグル/選択は押した瞬間が保存の合図になるが、確認テキストの
      // 更新だけでは「保存された」ことが伝わりにくいため)。
      appToaster.create({
        type: 'success',
        title: t('page:adminBidLimits.defaultWithdrawOffset.saveSuccess'),
      })
    } catch {
      appToaster.create({
        type: 'error',
        title: t('page:adminBidLimits.defaultWithdrawOffset.saveError'),
      })
    }
  }

  const isDisabled = isPending || isReadonly
  const disabledReason =
    readonlyReason ??
    (isPending ? t('page:adminBidLimits.savingTooltip') : undefined)

  return (
    <>
      {/* 8080 のカード補足は 14px (md)。 IntradayTab 内の他カードと揃える。 */}
      <Text marginBottom={3} color="Text.midEmphasis" fontSize="md">
        {t('page:adminBidLimits.defaultWithdrawOffset.description')}
      </Text>
      <Flex alignItems="center" flexWrap="wrap" gap={3}>
        {/* "1 時間 30 分前" が最長ラベルのため、それが収まる幅に固定する。
            無効時の理由は包みの DS Tooltip で出す (UI 辞書 action-reason.md の区分 2。
            native の title は使わない)。 */}
        <ButtonTooltip content={disabledReason}>
          <NativeSelectRoot
            flexShrink={0}
            width="220px"
            disabled={isDisabled}
            size="sm"
          >
            <NativeSelectField
              aria-label={t('page:adminBidLimits.defaultWithdrawOffset.label')}
              onChange={(e) =>
                void handleChange(
                  Number(e.target.value) as DefaultWithdrawOffset,
                )
              }
              value={currentValue}
            >
              {OFFSET_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </NativeSelectField>
            <NativeSelectIndicator />
          </NativeSelectRoot>
        </ButtonTooltip>
        {/* AC-BDL-502: 選択値に対応する確認テキストを常時表示する。 */}
        {currentOption && (
          <Text color="Text.midEmphasis" fontSize="sm">
            {t(currentOption.confirmKey)}
          </Text>
        )}
      </Flex>
      {/* AC-BDL-503: 「直前」(0分) 選択中のみ警告を表示する。 */}
      {isImmediate && (
        <Box marginTop={3}>
          <CompactAlert
            description={t(
              'page:adminBidLimits.defaultWithdrawOffset.warningBody',
            )}
            status="warning"
            title={t('page:adminBidLimits.defaultWithdrawOffset.warningTitle')}
          />
        </Box>
      )}
    </>
  )
}
